import React, { useRef } from 'react';

import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { clsx, wrapSquareBracketedWithEm } from '../../utils/utils';
import * as styles from './AnimatedTitle.module.scss';

export interface AnimatedTitleProps {
  title: string;
  trigger: string;
  className?: string;
  resolution?: 'mobile' | 'desktop' | 'all';
}

const AnimatedTitle = ({
  title,
  trigger,
  resolution,
  className,
}: AnimatedTitleProps): React.ReactElement => {
  const containerRef = useRef<HTMLHeadingElement | null>(null);

  useGSAP(() => {
    const emElements = containerRef.current?.querySelectorAll('em');
    if (!emElements?.length) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: `.${trigger}`,
        start: 'top 75%',
      } satisfies ScrollTrigger.Vars,
    });

    gsap.context(() => {
      // aria: "none" prevents SplitText from adding aria-label/aria-hidden.
      // aria-label is prohibited on elements without a role (em, span), and
      // without aria-hidden screen readers read the split chars as normal
      // sequential text, so the visible word is still announced correctly.
      const split = new SplitText(`em`, { type: 'chars', aria: 'none' });
      tl.from(split.chars, {
        y: 30,
        autoAlpha: 0,
        duration: 0.6,
        stagger: {
          amount: 0.6,
          ease: 'power1.in',
        },
      });
    }, containerRef);
  });

  return (
    <h2
      ref={containerRef}
      className={clsx(
        styles.title,
        resolution === 'mobile' && styles.mobile,
        resolution === 'desktop' && styles.desktop,
        (!resolution || resolution === 'all') && styles.alwaysVisible,
        className,
      )}
    >
      {wrapSquareBracketedWithEm(title)}
    </h2>
  );
};

export default AnimatedTitle;
